import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  clearedExportListFilters,
  defaultExportListFilters,
  exportListFiltersEqual,
  parseExportListFiltersFromSearchParams,
  serializeExportListFiltersToSearchParams,
  validateExportListFilters,
  type ExportListFilters,
} from "@/domain/export/filterExportJobs";

const DEFAULT_PAGE_SIZE = 20;

export function useExportListFilters() {
  const [params, setParams] = useSearchParams();

  const page = Math.max(0, Number(params.get("page") ?? "0") || 0);
  const pageSize = Math.min(
    50,
    Math.max(
      5,
      Number(params.get("page_size") ?? String(DEFAULT_PAGE_SIZE)) ||
        DEFAULT_PAGE_SIZE,
    ),
  );

  const applied = useMemo(
    () => parseExportListFiltersFromSearchParams(params),
    [params],
  );

  const [draft, setDraft] = useState<ExportListFilters>(applied);
  const paramsKey = params.toString();

  useEffect(() => {
    const next = parseExportListFiltersFromSearchParams(
      new URLSearchParams(paramsKey),
    );
    setDraft((current) =>
      exportListFiltersEqual(current, next) ? current : next,
    );
  }, [paramsKey]);

  const isDirty = useMemo(
    () => !exportListFiltersEqual(draft, applied),
    [draft, applied],
  );

  const setPage = useCallback(
    (next: number) => {
      const p = new URLSearchParams(params);
      p.set("page", String(Math.max(0, next)));
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  const setPageSize = useCallback(
    (size: number) => {
      const p = new URLSearchParams(params);
      p.set("page_size", String(size));
      p.set("page", "0");
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  const patchDraft = useCallback((patch: Partial<ExportListFilters>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const commitSearch = useCallback(() => {
    if (validateExportListFilters(draft).length > 0) return;
    const p = serializeExportListFiltersToSearchParams(draft, params);
    p.set("page", "0");
    setParams(p, { replace: true });
  }, [draft, params, setParams]);

  const resetFilters = useCallback(() => {
    const cleared = clearedExportListFilters();
    const p = new URLSearchParams(params);
    p.delete("q");
    p.delete("status");
    p.set("date_from", "");
    p.set("date_to", "");
    p.set("page", "0");
    setParams(p, { replace: true });
    setDraft(cleared);
  }, [params, setParams]);

  return {
    page,
    pageSize,
    /** Applied filters from the URL (drives list + stats queries). */
    filters: applied,
    applied,
    draft,
    isDirty,
    setPage,
    setPageSize,
    patchDraft,
    commitSearch,
    resetFilters,
    defaultFilters: defaultExportListFilters(),
  };
}
